import { ref } from 'vue'

import { VAPID_PUBLIC_KEY } from '../config/push'
import { pushApi } from '../services/pushApi'

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(base64)
  const output = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; ++i) output[i] = raw.charCodeAt(i)
  return output
}

export function usePushSubscribe() {
  const supported =
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window

  const subscribed = ref(false)
  const busy = ref(false)

  async function refresh() {
    if (!supported) return
    const reg = await navigator.serviceWorker.ready
    subscribed.value = !!(await reg.pushManager.getSubscription())
  }

  async function enable(): Promise<{ ok: boolean; reason?: string }> {
    if (!supported) return { ok: false, reason: 'unsupported' }
    busy.value = true
    try {
      if (Notification.permission !== 'granted') {
        const perm = await Notification.requestPermission()
        if (perm !== 'granted') return { ok: false, reason: 'denied' }
      }
      let sub: PushSubscription | null = null
      let createdHere = false
      try {
        const reg = await navigator.serviceWorker.ready
        sub = await reg.pushManager.getSubscription()
        if (!sub) {
          sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
          })
          createdHere = true
        }
        const json = sub.toJSON() as { keys?: { p256dh?: string; auth?: string } }
        await pushApi.subscribe({
          endpoint: sub.endpoint,
          keys: { p256dh: json.keys?.p256dh ?? '', auth: json.keys?.auth ?? '' },
          user_agent: navigator.userAgent,
        })
        subscribed.value = true
        return { ok: true }
      } catch {
        if (createdHere && sub) {
          try {
            await sub.unsubscribe()
          } catch {
            // best-effort rollback; ignore
          }
        }
        subscribed.value = false
        return { ok: false, reason: 'error' }
      }
    } finally {
      busy.value = false
    }
  }

  async function disable() {
    if (!supported) return
    busy.value = true
    try {
      const reg = await navigator.serviceWorker.ready
      const sub = await reg.pushManager.getSubscription()
      if (sub) {
        await pushApi.unsubscribe(sub.endpoint)
        await sub.unsubscribe()
      }
      subscribed.value = false
    } catch {
      subscribed.value = false
    } finally {
      busy.value = false
    }
  }

  return { supported, subscribed, busy, enable, disable, refresh }
}
